import { useCallback, useEffect, useState } from 'react';
import {
  GoogleLoginResponse,
  GoogleLoginResponseOffline,
  useGoogleLogin,
  useGoogleLogout,
} from 'react-google-login';
import { getRequiredEnv, t } from '../../../utils/helpers';
import { refreshGoogleToken } from '../utils/refreshGoogleToken';
import { IAuthService } from '@base/features/auth/model/interfaces';
import { container } from 'tsyringe';
import { checkUsername, saveResponse } from '../utils/helpers';
import { useHistory } from 'react-router-dom';
import { ResponseCode } from '../../../utils/constants';
import { User } from '../model/interfaces';
import { MAIN_ROUTES } from '../../routing/constants/mainRoutes';
import { FromSocial, OAuthProvider } from '../model/const';
import { IModalProvider } from '@base/features/modal/model/interfaces';

const googleAppId = getRequiredEnv('NX_GOOGLE_APP_ID');

export interface GoogleLoginState {
  isProcessing: boolean;
  isLoggedIn: boolean;
  currentUser?: User;
  loaded: boolean;
  accessToken?: string;
  tokenId?: string;
}

type UseGoogleAuthType = {
  loginGoogle: () => void;
  logoutGoogle: () => void;
  isProcessing: boolean;
};

function isGoogleLoginResponseOffline(object: any): object is GoogleLoginResponseOffline {
  return Object.prototype.hasOwnProperty.call(object, 'code');
}

export function useGoogleAuth(): UseGoogleAuthType {
  const authService: IAuthService = container.resolve('authService');
  const modalProvider: IModalProvider = container.resolve('modalProvider');
  const history = useHistory();

  const [state, setState] = useState<GoogleLoginState>({
    isProcessing: false,
    isLoggedIn: false,
    loaded: false,
  });
  const { accessToken, currentUser, tokenId, isProcessing, loaded } = state;

  const goGooglePage = useCallback(() => {
    const fromGoogle: FromSocial = {
      from: OAuthProvider.GOOGLE,
    };
    history.push(MAIN_ROUTES.changeSocialUsername, fromGoogle);
  }, [history]);

  const sendTokenOnServer = useCallback(
    async (accessToken: string, tokenId: string, email: string, name: string) => {
      try {
        const res = await authService.doLoginGoogle(accessToken, tokenId, email, name);
        if (res.code === ResponseCode.SUCCESS) {
          const { usr } = res;
          if (checkUsername(usr)) {
            const userData = {
              res,
              user: state.currentUser,
            };
            saveResponse(userData);
            goGooglePage();
          } else {
            authService.doEntryDefault(res);
          }
        } else {
          modalProvider.show({
            title: t('error'),
            body: t('unknown_error_try_again'),
            buttons: [{ title: t('ok'), onClick: () => modalProvider.hide() }],
          });
        }
      } catch (e) {
        console.error('doLoginGoogle error', e);
        modalProvider.show({
          title: t('error'),
          body: t('unknown_error_try_again'),
          buttons: [{ title: t('ok'), onClick: () => modalProvider.hide() }],
        });
      }
    },
    [state.currentUser]
  );

  useEffect(() => {
    if (currentUser && accessToken && tokenId) {
      setState((prevState) => {
        return {
          ...prevState,
          isProcessing: true,
        };
      });
      sendTokenOnServer(accessToken, tokenId, currentUser.email, currentUser.name);
    } else {
      setState((prevState) => {
        return {
          ...prevState,
          isProcessing: false,
        };
      });
    }
  }, [accessToken, currentUser, tokenId, loaded]);

  const onSuccess = (response: GoogleLoginResponse | GoogleLoginResponseOffline) => {
    if (isGoogleLoginResponseOffline(response)) {
      console.info('isGoogleLoginResponseOffline', response);
      setState((prevState) => {
        return {
          ...prevState,
          isProcessing: false,
        };
      });
    } else {
      const { accessToken, tokenId } = response;
      const { email, name, googleId, imageUrl } = response.profileObj;

      const userData: User = {
        id: googleId,
        name: name,
        email: email,
        picture: {
          data: {
            url: imageUrl,
          },
        },
      };

      setState({
        ...state,
        isLoggedIn: true,
        currentUser: userData,
        isProcessing: false,
        loaded: true,
        accessToken,
        tokenId,
      });
      refreshGoogleToken(response);
    }
  };

  const onLogoutSuccess = () => {
    console.info('Logged out Success');
  };

  const onFailure = (res: any) => {
    console.error('onFailure google', res);
    setState((prevState) => {
      return {
        ...prevState,
        isProcessing: false,
      };
    });
    //userService.showModal('ERROR', res.error);
  };

  const { signIn } = useGoogleLogin({
    onSuccess,
    onFailure,
    clientId: googleAppId,
    accessType: 'offline',
  });

  const { signOut } = useGoogleLogout({
    clientId: googleAppId,
    onLogoutSuccess,
  });

  const loginGoogle = () => {
    setState((prevState) => {
      return {
        ...prevState,
        isProcessing: true,
      };
    });
    signIn();
  };

  const logoutGoogle = () => {
    setState((prevState) => {
      delete prevState.currentUser;
      return prevState;
    });
    signOut();
  };

  return { loginGoogle, logoutGoogle, isProcessing };
}
